const CLIENT_KEY_STORAGE = 'c8_device_key';

let cachedVisitorId: string | null | undefined;
let visitorIdPromise: Promise<string | null> | null = null;

export function getOrCreateClientKey(): string {
  if (typeof window === 'undefined') return '';
  try {
    let key = localStorage.getItem(CLIENT_KEY_STORAGE);
    if (!key) {
      key = crypto.randomUUID();
      localStorage.setItem(CLIENT_KEY_STORAGE, key);
    }
    return key;
  } catch {
    return '';
  }
}

/** Warm FingerprintJS in the background so auth calls can include visitorId. */
export function preloadDeviceFingerprint(): void {
  if (typeof window === 'undefined') return;
  void getFingerprintJsVisitorId();
}

export async function getFingerprintJsVisitorId(): Promise<string | null> {
  if (cachedVisitorId !== undefined) return cachedVisitorId;
  if (!visitorIdPromise) {
    visitorIdPromise = (async () => {
      try {
        const FingerprintJS = await import('@fingerprintjs/fingerprintjs');
        const agent = await FingerprintJS.load();
        const result = await agent.get();
        cachedVisitorId = result.visitorId || null;
        return cachedVisitorId;
      } catch {
        cachedVisitorId = null;
        return null;
      }
    })();
  }
  return visitorIdPromise;
}

export type DeviceAuthPayload = {
  clientKey: string;
  visitorId?: string;
  platform: 'web';
};

export async function getDeviceAuthPayload(): Promise<DeviceAuthPayload | null> {
  const clientKey = getOrCreateClientKey();
  if (!clientKey) return null;
  const visitorId = await getFingerprintJsVisitorId();
  return {
    clientKey,
    ...(visitorId ? { visitorId } : {}),
    platform: 'web',
  };
}

/** Sync headers for authenticated API calls (verifysession, etc.). */
export function getDeviceHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const clientKey = getOrCreateClientKey();
  if (clientKey) headers['X-C8-Client-Key'] = clientKey;
  if (cachedVisitorId) headers['X-C8-Visitor-Id'] = cachedVisitorId;
  return headers;
}

/** Prefer for spin start/claim so FingerprintJS is included when available. */
export async function getDeviceHeadersAsync(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  const clientKey = getOrCreateClientKey();
  if (clientKey) headers['X-C8-Client-Key'] = clientKey;
  const visitorId = await getFingerprintJsVisitorId();
  if (visitorId) headers['X-C8-Visitor-Id'] = visitorId;
  return headers;
}
